import type {
  Message,
  MessageA2uiSurface,
  MessageGeneration,
  StreamBlock,
  StreamStatusEntry,
  StreamStatusEntryIcon,
} from '../types/chat';
import {
  resolveMessageA2uiApproval,
  upsertMessageA2uiSurface,
} from './parse-message-a2ui';
import { completeInProgressToolCalls } from './parse-message-tool-calls';
import {
  completeInProgressStatusEntries,
  resolveStreamActivityEntries,
} from './stream-status-entries';

export function joinStreamText(blocks: StreamBlock[] | undefined): string {
  if (!blocks?.length) return '';
  let text = '';
  for (const block of blocks) {
    if (block.type === 'text') text += block.text;
  }
  return text;
}

export function appendStreamText(
  blocks: StreamBlock[] | undefined,
  chunk: string
): StreamBlock[] {
  if (!chunk) return blocks ?? [];
  const list = [...(blocks ?? [])];
  const last = list[list.length - 1];
  if (last?.type === 'text') {
    list[list.length - 1] = { type: 'text', text: last.text + chunk };
    return list;
  }
  return [...list, { type: 'text', text: chunk }];
}

export function replaceStreamText(
  blocks: StreamBlock[] | undefined,
  text: string
): StreamBlock[] {
  if (!blocks?.length) {
    return text ? [{ type: 'text', text }] : [];
  }
  const firstTextIndex = blocks.findIndex((block) => block.type === 'text');
  if (firstTextIndex === -1) {
    return text ? [...blocks, { type: 'text', text }] : [...blocks];
  }
  const list: StreamBlock[] = [];
  let replaced = false;
  for (const block of blocks) {
    if (block.type === 'text') {
      if (!replaced && text) {
        list.push({ type: 'text', text });
        replaced = true;
      }
    } else {
      list.push(block);
    }
  }
  return list;
}

export function appendStreamStatus(
  blocks: StreamBlock[] | undefined,
  entry: StreamStatusEntry,
  pairWithIcon?: StreamStatusEntryIcon
): StreamBlock[] {
  const list = [...(blocks ?? [])];

  for (let i = 0; i < list.length; i += 1) {
    const block = list[i];
    if (block.type !== 'steps') continue;
    const existing = block.entries.findIndex((row) => row.id === entry.id);
    if (existing >= 0) {
      const entries = [...block.entries];
      entries[existing] = entry;
      list[i] = { type: 'steps', entries };
      return list;
    }
  }

  if (pairWithIcon) {
    for (let i = list.length - 1; i >= 0; i -= 1) {
      const block = list[i];
      if (block.type === 'steps' && block.entries.some((row) => row.icon === pairWithIcon)) {
        list[i] = { type: 'steps', entries: [...block.entries, entry] };
        return list;
      }
    }
  }

  // Models often stream a bare "\n\n" between tool calls; drop it so both calls stay in one Steps block.
  const last = list[list.length - 1];
  const beforeLast = list[list.length - 2];
  if (last?.type === 'text' && !last.text.trim() && beforeLast?.type === 'steps') {
    list.pop();
    list[list.length - 1] = { type: 'steps', entries: [...beforeLast.entries, entry] };
    return list;
  }

  if (last?.type === 'steps') {
    list[list.length - 1] = { type: 'steps', entries: [...last.entries, entry] };
    return list;
  }

  return [...list, { type: 'steps', entries: [entry] }];
}

export function upsertStreamSurface(
  blocks: StreamBlock[] | undefined,
  surface: MessageA2uiSurface,
  kind: 'surface' | 'update' = 'surface'
): StreamBlock[] {
  const blockType = surface.approval ? 'approval' : 'a2ui';
  const list = [...(blocks ?? [])];
  const index = list.findIndex(
    (block) =>
      (block.type === 'approval' || block.type === 'a2ui') &&
      block.surface.surfaceId === surface.surfaceId
  );
  if (index === -1) {
    return [...list, { type: blockType, surface }];
  }
  const existing = list[index];
  if (existing.type !== 'approval' && existing.type !== 'a2ui') return list;
  const merged = upsertMessageA2uiSurface([existing.surface], surface, kind)[0];
  list[index] = {
    type: merged.approval ? 'approval' : existing.type,
    surface: merged,
  };
  return list;
}

export function resolveStreamApprovalBlock(
  blocks: StreamBlock[] | undefined,
  resolved: { approvalId: string; decision: 'approved' | 'declined'; resolvedBy?: string }
): StreamBlock[] | undefined {
  if (!blocks?.length) return blocks;
  return blocks.map((block) => {
    if (block.type !== 'approval' && block.type !== 'a2ui') return block;
    if (block.surface.approval?.approvalId !== resolved.approvalId) return block;
    return {
      ...block,
      type: 'approval',
      surface: resolveMessageA2uiApproval([block.surface], resolved)[0],
    };
  });
}

export function markStreamSurfaceStatus(
  blocks: StreamBlock[] | undefined,
  surfaceId: string,
  status: MessageA2uiSurface['status']
): StreamBlock[] | undefined {
  if (!blocks?.length) return blocks;
  return blocks.map((block) => {
    if (
      (block.type === 'approval' || block.type === 'a2ui') &&
      block.surface.surfaceId === surfaceId
    ) {
      return { ...block, surface: { ...block.surface, status } };
    }
    return block;
  });
}

export function applyStreamAnswerText<T extends MessageGeneration>(
  generation: T,
  chunk: string,
  mode: 'append' | 'replace' = 'append'
): T {
  let baseBlocks = generation.streamBlocks;
  if (!baseBlocks?.length && generation.content) {
    baseBlocks = [{ type: 'text', text: generation.content }];
  }
  const streamBlocks =
    mode === 'replace'
      ? replaceStreamText(baseBlocks, chunk)
      : appendStreamText(baseBlocks, chunk);
  return {
    ...generation,
    content: joinStreamText(streamBlocks),
    streamBlocks,
  };
}

export function applyStreamStatusEntry<T extends MessageGeneration>(
  generation: T,
  entry: StreamStatusEntry,
  pairWithIcon?: StreamStatusEntryIcon
): T {
  return {
    ...generation,
    streamBlocks: appendStreamStatus(generation.streamBlocks, entry, pairWithIcon),
  };
}

export function applyStreamSurface<T extends MessageGeneration>(
  generation: T,
  surface: MessageA2uiSurface,
  kind: 'surface' | 'update' = 'surface'
): T {
  const streamBlocks = upsertStreamSurface(generation.streamBlocks, surface, kind);
  const a2uiSurfaces = upsertMessageA2uiSurface(generation.a2uiSurfaces, surface, kind);
  return { ...generation, streamBlocks, a2uiSurfaces };
}

export function applyStreamApprovalResolved<T extends MessageGeneration>(
  generation: T,
  resolved: { approvalId: string; decision: 'approved' | 'declined'; resolvedBy?: string }
): T {
  return {
    ...generation,
    streamBlocks: resolveStreamApprovalBlock(generation.streamBlocks, resolved),
    a2uiSurfaces: resolveMessageA2uiApproval(generation.a2uiSurfaces, resolved),
  };
}

export function generationHasPendingApproval(
  generation?: Pick<MessageGeneration, 'a2uiSurfaces' | 'streamBlocks'> | null
): boolean {
  const now = Date.now();
  const isPending = (surface: MessageA2uiSurface) => {
    if (!surface.approval || surface.status !== 'active') return false;
    const expiry = surface.approval.expiresAt
      ? Date.parse(surface.approval.expiresAt)
      : Number.NaN;
    return Number.isNaN(expiry) || expiry > now;
  };
  if (generation?.streamBlocks?.some((block) =>
    (block.type === 'approval' || block.type === 'a2ui') && isPending(block.surface)
  )) {
    return true;
  }
  return (generation?.a2uiSurfaces ?? []).some(isPending);
}

/**
 * History / reconnect: no persisted timeline, so use the saved metadata order —
 * Steps first, then the full answer, then approval/A2UI cards.
 */
export function hydrateStreamBlocks<T extends MessageGeneration>(generation: T): T {
  if (generation.streamBlocks?.length) return generation;

  const blocks: StreamBlock[] = [];

  const stepEntries = resolveStreamActivityEntries(generation).filter(
    (entry) => entry.icon !== 'processing_started'
  );
  if (stepEntries.length) {
    blocks.push({ type: 'steps', entries: stepEntries });
  }

  const text = generation.content ?? '';
  if (text) {
    blocks.push({ type: 'text', text });
  }

  for (const surface of generation.a2uiSurfaces ?? []) {
    blocks.push({
      type: surface.approval ? 'approval' : 'a2ui',
      surface,
    });
  }

  return blocks.length ? { ...generation, streamBlocks: blocks } : generation;
}

export function hasStepsStreamBlock(blocks: StreamBlock[] | undefined): boolean {
  return (blocks ?? []).some((block) => block.type === 'steps' && block.entries.length > 0);
}

export function hydrateMessageStreamBlocks(messages: Message[]): Message[] {
  return messages.map((message) => ({
    ...message,
    generations: message.generations.map((generation) => hydrateStreamBlocks(generation)),
  }));
}

/**
 * Returns stream blocks where any steps block with in_progress todos is marked completed.
 */
export function completeInProgressStreamBlocks(
  blocks?: StreamBlock[]
): StreamBlock[] | undefined {
  if (!blocks?.length) {
    return blocks;
  }
  return blocks.map((block) => {
    if (block.type !== 'steps') {
      return block;
    }
    return {
      ...block,
      entries: completeInProgressStatusEntries(block.entries) ?? block.entries,
    };
  });
}

/**
 * Strikes through (marks completed) all in-progress todos across streamStatusEntries,
 * streamBlocks, and toolCalls for a finished generation (e.g. on END_OF_STREAM).
 */
export function completeGenerationInProgressTodos<T extends MessageGeneration>(
  generation: T
): T {
  const streamStatusEntries = completeInProgressStatusEntries(generation.streamStatusEntries);
  const streamBlocks = completeInProgressStreamBlocks(generation.streamBlocks);
  const toolCalls = completeInProgressToolCalls(generation.toolCalls);

  return {
    ...generation,
    ...(generation.streamStatusEntries && { streamStatusEntries }),
    ...(generation.streamBlocks && { streamBlocks }),
    ...(generation.toolCalls && { toolCalls }),
  };
}

